import { type FC, useRef, useState } from 'react';

import { ArrowUpRight, ChevronDown, Expand } from 'lucide-react';

import { openLightbox } from '@shared/lib/openLightbox';
import { tokens } from '@shared/styles/tokens';
import type { IProject } from '@shared/types';
import { Picture } from '@shared/ui/Picture';

import styles from './ProjectCard.module.css';

interface ProjectCardProps {
  project: IProject;
  featured?: boolean;
}

export const ProjectCard: FC<ProjectCardProps> = ({ project, featured = false }) => {
  const cover = project.photos[0];
  const openingRef = useRef(false);
  const [isOpening, setIsOpening] = useState(false);

  const handleOpenPhotos = async () => {
    // Ref защищает и от двух кликов в одном кадре до применения setState.
    if (openingRef.current) return;
    openingRef.current = true;
    setIsOpening(true);
    try {
      await openLightbox(
        project.photos.map((photo) => ({
          ...photo,
          msrc: photo.thumbnail,
          alt: `${photo.alt}. Фото: ${project.photoCredit}`,
        })),
        0,
        project.title,
      );
    } finally {
      openingRef.current = false;
      setIsOpening(false);
    }
  };

  return (
    <article className={styles.root} aria-labelledby={`${project.id}-title`}>
      <figure className={styles.figure}>
        <button
          className={styles.photoButton}
          type="button"
          onClick={handleOpenPhotos}
          aria-label={`Смотреть фото · ${project.photos.length}: ${project.location}`}
          aria-busy={isOpening}
        >
          <Picture
            className={styles.image}
            sizes={`(max-width: ${tokens.breakpoints.tablet}) calc(100vw - 2 * ${tokens.space.pageBase}), calc((min(100vw, ${tokens.layout.contentMax}) - 2 * ${tokens.space.pageBase} - ${tokens.space.projectGap}) * 6 / 11)`}
            src={cover.src}
            width={cover.width}
            height={cover.height}
            alt={cover.alt}
            priority={featured}
          />
          <span className={styles.photoAction} aria-hidden="true">
            <Expand size={16} />{' '}
            {isOpening ? 'Открываем…' : `Смотреть фото · ${project.photos.length}`}
          </span>
        </button>
        <figcaption className={styles.credit}>
          Фото: <a href={project.photoSourceUrl}>{project.photoCredit}</a>
        </figcaption>
      </figure>
      <div className={styles.body}>
        <div className={styles.meta}>
          <span>{project.region}</span>
          <span>{project.year}</span>
        </div>
        <h2 className={styles.title} id={`${project.id}-title`}>
          {project.title}
        </h2>
        <p className={styles.location}>{project.location}</p>
        <p className={styles.description}>{project.description}</p>
        <ul className={styles.equipment} aria-label="Оборудование">
          {project.equipment.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <details className={styles.sources}>
          <summary>
            Источники об объекте <ChevronDown size={16} aria-hidden="true" />
          </summary>
          <ul className={styles.sourceList}>
            {project.sources.map((source) => (
              <li key={source.url}>
                <a href={source.url}>
                  {source.label} <ArrowUpRight size={14} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
          <p className={styles.contract}>
            Контракт ЕИС №{' '}
            <a
              href={`https://zakupki.gov.ru/epz/contract/contractCard/common-info.html?reestrNumber=${project.contractId}`}
            >
              {project.contractId}
            </a>
          </p>
        </details>
      </div>
    </article>
  );
};
